/**
 * Client mirror of api/src/utils/siteTypes.js — keep the two in step, as with
 * utils/coordinates.js and the permissions mirror.
 *
 * Gareth 2026-08-23: a site is a Dairy or a Beef unit, and that decides which
 * details it records. Dairy is the default and the historic behaviour.
 *
 * LIST-SHAPED ON PURPOSE. Gareth chose "one type per site for now, both later",
 * so siteTypeKeys returns an array holding one entry today. Mixed sites then
 * become an extension rather than a rewrite — do not collapse to a scalar.
 */

export const DAIRY = 'dairy';
export const BEEF = 'beef';

export const SITE_TYPES = {
  [DAIRY]: {
    key: DAIRY,
    label: 'Dairy',
    // Shown under the picker so the choice explains itself.
    hint: 'Milking herd — records parlour, milking schedule and herd figures.',
    fields: [
      'parlour_type', 'parlour_manufacturer', 'parlour_manufacturer_other',
      'parlour_size', 'rotation_direction', 'parlour_speed_secs_per_stall',
      'milkings_per_day', 'milking_start_times', 'milking_end_times',
      'cows_onsite', 'cows_milking',
      'average_yield_litres', 'milk_buyer', 'milk_contract_type'
    ],
    headCountField: 'cows_milking',
    headCountLabel: 'Cows milking'
  },
  [BEEF]: {
    key: BEEF,
    label: 'Beef',
    hint: 'Beef cattle — no parlour or milking schedule. Counted separately from milking cows.',
    fields: ['cattle_onsite'],
    headCountField: 'cattle_onsite',
    headCountLabel: 'Cattle on site'
  }
};

export const DEFAULT_SITE_TYPE = DAIRY;
export const SITE_TYPE_KEYS = Object.keys(SITE_TYPES);

/** Every type-specific field across all types. */
export const ALL_TYPE_FIELDS = [...new Set(
  SITE_TYPE_KEYS.flatMap(key => SITE_TYPES[key].fields)
)];

export function normaliseSiteType(value) {
  return SITE_TYPES[value] ? value : DEFAULT_SITE_TYPE;
}

/** Array today, one entry — see the header. */
export function siteTypeKeys(siteType) {
  return [normaliseSiteType(siteType)];
}

export function fieldsForSiteType(siteType) {
  return [...new Set(siteTypeKeys(siteType).flatMap(key => SITE_TYPES[key].fields))];
}

/** Does a site of this type record this field? Drives what the form shows. */
export function recordsField(siteType, field) {
  return fieldsForSiteType(siteType).includes(field);
}

/**
 * Does this type record ANY of these fields? Used to decide whether a whole
 * form section (Parlour, Milking Schedule, Herd) is worth rendering.
 */
export function recordsAnyField(siteType, fields) {
  const applicable = fieldsForSiteType(siteType);
  return fields.some(field => applicable.includes(field));
}

export function siteTypeLabel(siteType) {
  return SITE_TYPES[normaliseSiteType(siteType)].label;
}
